import { useCallback } from "react";
import { useAppSelector } from "@redux/hooks";
import { useExternalProviderRepository } from "../context/external-provider-repository.context";
import { uploadDocument } from "@shared-kernel/application/use-cases/document-upload/upload-document";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { useApiAction } from "@shared-kernel/primary/shared/api-errors/use-api-action";
import { applyApiFieldErrors } from "@shared-kernel/primary/shared/api-errors/apply-api-field-errors";
import { useFieldUpload } from "@shared-kernel/primary/shared/api-errors/use-field-upload";
import type { ActionForm } from "@shared-kernel/primary/shared/api-errors/action-form";
import { useResetWhenSettled } from "@shared-kernel/primary/shared/request-state/use-reset-when-settled";
import {
  resetUploadSpeakerResume,
  uploadSpeakerResume,
} from "@academy-context/write/application/use-cases/external-provider/speaker-resume-upload/upload-speaker-resume";
import {
  submitRequestData,
  resetSubmitRequestData,
} from "@academy-context/write/application/use-cases/external-provider/request-data-submission/submit-request-data";
import { selectExternalProviderSpeakerResumeUpload } from "@academy-context/write/application/use-cases/external-provider/speaker-resume-upload/selectors/external-provider-speaker-resume-upload-selectors";
import { selectExternalProviderRequestDataSubmission } from "@academy-context/write/application/use-cases/external-provider/request-data-submission/selectors/external-provider-request-data-submission-selectors";
import { formatFormDataToBody, type RequestDataFormInputs } from "../form-validation/request-data-form.schema";

export const useRequestDataSubmit = (form: ActionForm<RequestDataFormInputs>, onSuccess: () => void, requestId: string) => {
  const run = useApiAction();
  const repository = useExternalProviderRepository();
  const upload = useFieldUpload(form);

  const { processing: uploadProcessing } = useAppSelector(selectExternalProviderSpeakerResumeUpload);
  useResetWhenSettled(uploadProcessing, resetUploadSpeakerResume);
  const { processing: submitProcessing } = useAppSelector(selectExternalProviderRequestDataSubmission);
  useResetWhenSettled(submitProcessing, resetSubmitRequestData);

  // L'API dit pourquoi le fichier est refusé (PDF invalide, trop volumineux). Sans feedback : le refus s'affiche sous le champ
  const handleUploadSpeakerResume = useCallback(
    (rowIndex: number, file: File) => {
      void upload(
        `speakers.${rowIndex}.resumeUrl`,
        run(uploadSpeakerResume({ file, externalProviderRepository: repository })),
        "Erreur lors du téléchargement du CV",
      );
    },
    [upload, run, repository],
  );

  // Sans feedback : le refus d'une pièce jointe s'affiche sous la liste des documents. Rôle élève comme sur le formulaire de l'élève
  const handleUploadAttachment = useCallback(
    (file: File) => run(uploadDocument({ document: file, asAdmin: false, role: ROLES.STUDENT })),
    [run],
  );

  // L'API dit pourquoi les données sont refusées (demande déjà complétée)
  const handleSubmitRequestData = useCallback(
    async (values: RequestDataFormInputs) => {
      const outcome = await run(
        submitRequestData({ requestId, body: formatFormDataToBody(values), externalProviderRepository: repository }),
        {
          success: "Données soumises avec succès ! En attente de validation admin.",
          failure: "Erreur lors de la soumission des données",
        },
      );
      if (!outcome.ok) {
        applyApiFieldErrors(form, outcome.error);
        return;
      }
      onSuccess();
    },
    [run, requestId, repository, form, onSuccess],
  );

  return {
    uploadSpeakerResume: handleUploadSpeakerResume,
    uploadAttachment: handleUploadAttachment,
    submitRequestData: handleSubmitRequestData,
    uploadProcessing,
  };
};
